import type { InterviewSectionSettings } from "@repo/app-types/entities/interviewSection/settings";
import { InterviewSectionType } from "@repo/app-types/entities/interviewSection/types";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { IconPlus } from "@tabler/icons-react";
import { useMemo, useState, type FC, type ReactNode } from "react";
import { DisclosureGroup } from "react-aria-components";
import { useAllInterviewSectionDefinitionsForJobTypeQuery } from "../../../api/entities/interviewSectionDefinition.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import type {
  QuestionsDispatch,
  QuestionWithTemporaryId,
} from "../../projects/recruitingQuestions/questionsStore.js";
import { BaseInterviewSectionDefinitionCard } from "./card.js";
import { InterviewSectionCardSkeleton } from "./cardSkeleton.js";
import { ChatQuizInterviewSectionDefinitionCard } from "./chatQuiz/card.js";
import { useInterviewSectionCreateFlow } from "./createFlow.js";
import { KnowledgeTestInterviewSectionDefinitionCard } from "./knowledgeTest/card.js";
import { QuestionsInterviewSectionDefinitionCard } from "./questions/card.js";
import { RolePlayInterviewSectionDefinitionCard } from "./rolePlay/card.js";
import {
  applyInterviewSectionDefinitionChanges,
  type ChangedInterviewSectionDefinition,
  type VisibleInterviewSectionDefinition,
} from "./state.js";

export interface InterviewSectionDefinitionListPanelProps {
  projectId: string;
  jobTypeId: string;
  initialExpandedSectionId?: string;
  changedSections: readonly ChangedInterviewSectionDefinition[];
  onSectionCreate: (settings: InterviewSectionSettings) => string;
  onSectionUpdate: (id: string, settings: InterviewSectionSettings) => void;
  onSectionDelete: (id: string) => void;
  questions: readonly QuestionWithTemporaryId[] | undefined;
  onQuestionsChange: QuestionsDispatch | undefined;
}

const implicitQuestionsInterviewSectionDefinitionId = "__job_type_questions";

/**
 * Right-side panel after the introduction of interview sections.
 */
export const InterviewSectionDefinitionListPanel: FC<
  InterviewSectionDefinitionListPanelProps
> = (props) => {
  const { data: fetchedSections, isLoading: isFetchingSections } =
    useAllInterviewSectionDefinitionsForJobTypeQuery({
      jobTypeId: props.jobTypeId,
    });

  const [expandedId, setExpandedId] = useState<string | undefined>(
    props.initialExpandedSectionId ??
      implicitQuestionsInterviewSectionDefinitionId,
  );

  // Reconcile what we fetched from the server and the changes made by the user.
  const sections = useMemo(
    () =>
      applyInterviewSectionDefinitionChanges(
        fetchedSections,
        props.changedSections,
      ),
    [fetchedSections, props.changedSections],
  );

  const onSectionCreate = (settings: InterviewSectionSettings) => {
    const id = props.onSectionCreate(settings);
    setExpandedId(id);
  };

  const { renderInterviewSectionCreateTrigger } = useInterviewSectionCreateFlow(
    {
      isTrainingProject: false,
      title: "Add section",
      description:
        "Choose the type of interview section to add to your interview plan.",
      onSectionCreate,
    },
  );

  const renderSectionCard = (
    section: Pick<VisibleInterviewSectionDefinition, "id" | "settings">,
  ): ReactNode => {
    switch (section.settings.type) {
      case InterviewSectionType.Questions:
        return (
          <QuestionsInterviewSectionDefinitionCard
            // TODO: Feed a real interview section definition ID here.
            interviewSectionDefinitionId={
              implicitQuestionsInterviewSectionDefinitionId
            }
            projectId={props.projectId}
            questions={props.questions}
            onQuestionsChange={props.onQuestionsChange}
          />
        );

      case InterviewSectionType.RolePlay:
        return (
          <RolePlayInterviewSectionDefinitionCard
            key={section.id}
            interviewSectionDefinitionId={section.id}
            settings={section.settings}
            onChange={props.onSectionUpdate}
            onDelete={props.onSectionDelete}
          />
        );

      case InterviewSectionType.KnowledgeTest:
        return (
          <KnowledgeTestInterviewSectionDefinitionCard
            key={section.id}
            interviewSectionDefinitionId={section.id}
            settings={section.settings}
            onChange={props.onSectionUpdate}
            onDelete={props.onSectionDelete}
          />
        );

      case InterviewSectionType.ChatQuiz:
        return (
          <ChatQuizInterviewSectionDefinitionCard
            key={section.id}
            interviewSectionDefinitionId={section.id}
            settings={section.settings}
            onChange={props.onSectionUpdate}
            onDelete={props.onSectionDelete}
          />
        );

      default:
        return assertUnreachable(section.settings, () => (
          <BaseInterviewSectionDefinitionCard
            key={section.id}
            interviewSectionDefinitionId={section.id}
            title="Unknown section"
            subtitle=""
            onDelete={props.onSectionDelete}
          >
            <div className="p-3 text-sm text-foreground">
              This section type is not supported.
            </div>
          </BaseInterviewSectionDefinitionCard>
        ));
    }
  };

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex items-center justify-between bg-background px-3 py-2">
        <div>
          <div className="flex flex-row items-center space-x-2">
            <Label size={Size.MEDIUM}>Interview plan</Label>
          </div>
          <div className="text-xs text-gray-600">
            The AI interviewer will conduct the interview based on this plan.
          </div>
        </div>

        {renderInterviewSectionCreateTrigger(
          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.JOB_OPENINGS}
            passThrough={true}
          >
            {(allowed) => (
              <Button
                variant={ButtonVariant.Secondary}
                icon={<IconPlus className="size-4" />}
                isDisabled={!allowed}
              >
                Add section
              </Button>
            )}
          </Can>,
        )}
      </div>

      <DisclosureGroup
        expandedKeys={expandedId === undefined ? [] : [expandedId]}
        onExpandedChange={(keys) => {
          const key = keys.values().next().value;
          setExpandedId(typeof key === "string" ? key : undefined);
        }}
        className="flex-1 space-y-3 overflow-y-auto bg-background p-3"
      >
        {props.questions && props.onQuestionsChange
          ? renderSectionCard({
              id: implicitQuestionsInterviewSectionDefinitionId,
              settings: { type: InterviewSectionType.Questions },
            })
          : null}

        {sections?.map(renderSectionCard)}

        {isFetchingSections ? (
          <>
            <InterviewSectionCardSkeleton />
            <InterviewSectionCardSkeleton />
          </>
        ) : null}
      </DisclosureGroup>
    </div>
  );
};
